import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft, CircleNotch, ArrowsClockwise, Play, Gear,
  Robot, Gavel, Buildings, Envelope, Phone, Lightning,
  MagnifyingGlass, Clock, CheckCircle, XCircle, Eye,
  ArrowRight, CaretDown, CaretUp, ChatText, PaperPlaneTilt
} from '@phosphor-icons/react';

export default function B2BAutoPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [bids, setBids] = useState([]);
  const [discoveries, setDiscoveries] = useState([]);
  const [smsStats, setSmsStats] = useState(null);
  const [smsMessages, setSmsMessages] = useState([]);
  const [outboundStats, setOutboundStats] = useState(null);
  const [outboundCalls, setOutboundCalls] = useState([]);
  const [runningTask, setRunningTask] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, logsRes, bidsRes, discRes, smsStRes, smsMsgRes, obStRes, obCallsRes] = await Promise.all([
        axios.get(`${API_URL}/api/b2b/auto/config`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/b2b/auto/logs?limit=20`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/b2b/auto/bids?limit=30`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/b2b/auto/discoveries?limit=30`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/sms/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/sms/messages?limit=30`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/outbound/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/outbound/calls?limit=20`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setConfig(cfgRes.data);
      setLogs(logsRes.data);
      setBids(bidsRes.data);
      setDiscoveries(discRes.data);
      setSmsStats(smsStRes.data);
      setSmsMessages(smsMsgRes.data);
      setOutboundStats(obStRes.data);
      setOutboundCalls(obCallsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runTask = async (task) => {
    setRunningTask(task);
    try {
      await axios.post(`${API_URL}/api/b2b/auto/run-now`, { task }, { withCredentials: true });
      setTimeout(fetchAll, 5000);
    } catch (e) { console.error(e); }
    finally { setTimeout(() => setRunningTask(null), 3000); }
  };

  const createProspect = async (discoveryId) => {
    try {
      await axios.post(`${API_URL}/api/b2b/auto/discoveries/${discoveryId}/create-prospect`, {}, { withCredentials: true });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const tabs = [
    { id: 'dashboard', label: 'Panel', icon: Robot },
    { id: 'bids', label: 'Licitaciones', icon: Gavel },
    { id: 'discoveries', label: 'Descubrimientos', icon: Buildings },
    { id: 'sms', label: 'SMS', icon: ChatText },
    { id: 'calls', label: 'Llamadas', icon: Phone },
    { id: 'config', label: 'Config', icon: Gear },
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="b2b-auto-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/prospects')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">B2B Automatizacion</h1>
            <p className="text-sm text-slate-500">Busqueda automatica de leads, SMS, llamadas outbound</p>
          </div>
        </div>
        <button onClick={fetchAll} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><ArrowsClockwise size={20} /></button>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${activeTab === t.id ? 'bg-white shadow text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <DashboardTab config={config} logs={logs} smsStats={smsStats} outboundStats={outboundStats} bids={bids} discoveries={discoveries} runTask={runTask} runningTask={runningTask} />}
      {activeTab === 'bids' && <BidsTab bids={bids} />}
      {activeTab === 'discoveries' && <DiscoveriesTab discoveries={discoveries} createProspect={createProspect} />}
      {activeTab === 'sms' && <SMSTab stats={smsStats} messages={smsMessages} />}
      {activeTab === 'calls' && <CallsTab stats={outboundStats} calls={outboundCalls} />}
      {activeTab === 'config' && <ConfigTab config={config} onSave={fetchAll} />}
    </div>
  );
}

function DashboardTab({ config, logs, smsStats, outboundStats, bids, discoveries, runTask, runningTask }) {
  const isEnabled = config?.enabled !== false;
  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      <div className={`rounded-xl p-5 flex items-center justify-between ${isEnabled ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'}`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isEnabled ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}><Robot size={24} weight="fill" /></div>
          <div>
            <p className="font-semibold text-slate-900">B2B Auto Scheduler</p>
            <p className="text-sm text-slate-500">{isEnabled ? 'Activo - Busca licitaciones y prospectos automaticamente' : 'Inactivo - Activa desde la pestana Config'}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${isEnabled ? 'bg-emerald-500 text-white' : 'bg-slate-300 text-slate-600'}`}>{isEnabled ? 'ON' : 'OFF'}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Licitaciones Encontradas" value={bids.length} icon={Gavel} color="text-amber-600 bg-amber-50" />
        <StatCard label="Empresas Descubiertas" value={discoveries.length} icon={Buildings} color="text-purple-600 bg-purple-50" />
        <StatCard label="SMS Enviados" value={smsStats?.total_sent || 0} icon={ChatText} color="text-blue-600 bg-blue-50" />
        <StatCard label="Llamadas Outbound" value={outboundStats?.total_calls || 0} icon={Phone} color="text-green-600 bg-green-50" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Play size={16} className="text-blue-500" /> Ejecutar Ahora</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { task: 'bid_search', label: 'Buscar Licitaciones', desc: 'Busca bids y RFPs en Florida', icon: Gavel, color: 'border-amber-200 hover:bg-amber-50' },
            { task: 'prospect_discovery', label: 'Descubrir Empresas', desc: 'Busca empresas por sector', icon: MagnifyingGlass, color: 'border-purple-200 hover:bg-purple-50' },
            { task: 'auto_followup', label: 'Follow-ups Automaticos', desc: 'Enviar emails a prospectos sin contacto reciente', icon: Envelope, color: 'border-blue-200 hover:bg-blue-50' },
          ].map(t => (
            <button key={t.task} onClick={() => runTask(t.task)} disabled={runningTask} data-testid={`run-${t.task}`}
              className={`p-4 border rounded-xl text-left transition-all ${t.color} ${runningTask === t.task ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <t.icon size={18} className="text-slate-600" />
                <span className="text-sm font-semibold text-slate-900">{t.label}</span>
              </div>
              <p className="text-xs text-slate-500">{t.desc}</p>
              {runningTask === t.task && <p className="text-xs text-blue-600 mt-2 flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> Ejecutando...</p>}
            </button>
          ))}
        </div>
      </div>

      {logs.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Clock size={16} /> Historial de Ejecucion</h3>
          <div className="space-y-2">
            {logs.slice(0, 10).map((log, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${log.task === 'bid_search' ? 'bg-amber-100 text-amber-700' : log.task === 'prospect_discovery' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {log.task === 'bid_search' ? 'Licitaciones' : log.task === 'prospect_discovery' ? 'Descubrimiento' : 'Follow-up'}
                  </span>
                  <span className="text-slate-600">
                    {log.results_found !== undefined ? `${log.results_found} resultados` : log.emails_sent !== undefined ? `${log.emails_sent} emails enviados` : ''}
                  </span>
                </div>
                <span className="text-xs text-slate-400">{new Date(log.created_at).toLocaleString('es')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${color}`}><Icon size={18} weight="fill" /></div>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function BidsTab({ bids }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="bids-tab">
      <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Gavel size={20} /> Licitaciones Encontradas ({bids.length})</h2>
      {bids.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <Gavel size={40} className="mx-auto mb-3 opacity-50" />
          <p>Sin licitaciones aun. Ejecuta "Buscar Licitaciones" desde el Panel.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bids.map(bid => (
            <div key={bid.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900">{bid.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{bid.organization} | {bid.location}</p>
                  <div className="flex gap-3 mt-2 text-xs text-slate-500">
                    {bid.deadline && <span>Deadline: {bid.deadline}</span>}
                    {bid.estimated_value && <span>Valor: {bid.estimated_value}</span>}
                    <span>Relevancia: {bid.relevance_score}/10</span>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${bid.status === 'new' ? 'bg-blue-100 text-blue-700' : bid.status === 'reviewed' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                  {bid.status === 'new' ? 'Nuevo' : bid.status === 'reviewed' ? 'Revisado' : bid.status}
                </span>
              </div>
              {bid.url && <a href={bid.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"><Eye size={12} /> Ver detalle</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DiscoveriesTab({ discoveries, createProspect }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="discoveries-tab">
      <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Buildings size={20} /> Empresas Descubiertas ({discoveries.length})</h2>
      {discoveries.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <Buildings size={40} className="mx-auto mb-3 opacity-50" />
          <p>Sin descubrimientos aun. Ejecuta "Descubrir Empresas" desde el Panel.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {discoveries.map(d => (
            <div key={d.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900">{d.company_name}</h3>
                  <p className="text-sm text-slate-500">{d.industry} | {d.location}</p>
                  <p className="text-xs text-slate-400 mt-1">Servicios: {d.services_needed}</p>
                  <p className="text-xs text-slate-400">Approach: {d.contact_approach}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${d.compatibility_score >= 75 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      Score: {d.compatibility_score}%
                    </span>
                    {d.website && <a href={d.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">{d.website}</a>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${d.status === 'discovered' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {d.status === 'discovered' ? 'Descubierto' : 'Prospecto Creado'}
                  </span>
                  {d.status === 'discovered' && (
                    <button onClick={() => createProspect(d.id)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 flex items-center gap-1" data-testid={`create-prospect-${d.id}`}>
                      <ArrowRight size={12} /> Crear Prospecto
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SMSTab({ stats, messages }) {
  const [showSend, setShowSend] = useState(false);
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const sendSMS = async () => {
    if (!to || !body) return;
    setSending(true);
    setResult(null);
    try {
      await axios.post(`${API_URL}/api/sms/send`, { to, body }, { withCredentials: true });
      setResult({ ok: true, msg: 'SMS enviado' });
      setTo('');
      setBody('');
    } catch (e) {
      setResult({ ok: false, msg: e.response?.data?.detail || 'Error' });
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="sms-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Enviados" value={stats?.total_sent || 0} icon={PaperPlaneTilt} color="text-blue-600 bg-blue-50" />
        <StatCard label="Recibidos" value={stats?.total_received || 0} icon={ChatText} color="text-green-600 bg-green-50" />
        <StatCard label="Fallidos" value={stats?.total_failed || 0} icon={XCircle} color="text-red-600 bg-red-50" />
        <StatCard label="Opt-out" value={stats?.opted_out || 0} icon={XCircle} color="text-slate-600 bg-slate-50" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-900">Enviar SMS</h3>
          <button onClick={() => setShowSend(!showSend)} className="text-blue-600 text-sm" data-testid="toggle-send-sms">{showSend ? 'Cerrar' : 'Nuevo SMS'}</button>
        </div>
        {showSend && (
          <div className="space-y-3 mb-4 p-4 bg-slate-50 rounded-lg">
            <input type="text" value={to} onChange={e => setTo(e.target.value)} placeholder="Numero (ej: +1305555xxxx)" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="sms-to-input" />
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Mensaje..." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm h-20 resize-none" data-testid="sms-body-input" />
            {result && <p className={`text-sm ${result.ok ? 'text-green-600' : 'text-red-600'}`}>{result.msg}</p>}
            <button onClick={sendSMS} disabled={sending || !to || !body} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="send-sms-btn">
              {sending ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />} {sending ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Historial de Mensajes ({messages.length})</h3>
        {messages.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">Sin mensajes aun</p>
        ) : (
          <div className="space-y-2">
            {messages.map((msg, i) => (
              <div key={i} className={`p-3 rounded-lg text-sm ${msg.direction === 'inbound' ? 'bg-green-50 border-l-4 border-green-400' : 'bg-blue-50 border-l-4 border-blue-400'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${msg.direction === 'inbound' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {msg.direction === 'inbound' ? 'Recibido' : 'Enviado'}
                    </span>
                    <span className="text-slate-600">{msg.direction === 'inbound' ? msg.from_number : msg.to_number}</span>
                    {msg.related_to_name && <span className="text-xs text-slate-400">({msg.related_to_name})</span>}
                  </div>
                  <span className="text-xs text-slate-400">{new Date(msg.created_at).toLocaleString('es')}</span>
                </div>
                <p className="text-slate-700">{msg.body}</p>
                <span className={`text-xs mt-1 ${msg.status === 'failed' ? 'text-red-500' : 'text-slate-400'}`}>{msg.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CallsTab({ stats, calls }) {
  return (
    <div className="space-y-6" data-testid="calls-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Llamadas" value={stats?.total_calls || 0} icon={Phone} color="text-green-600 bg-green-50" />
        <StatCard label="Hoy" value={stats?.today || 0} icon={Clock} color="text-blue-600 bg-blue-50" />
        <StatCard label="Iniciadas" value={stats?.initiated || 0} icon={CheckCircle} color="text-emerald-600 bg-emerald-50" />
        <StatCard label="Fallidas" value={stats?.failed || 0} icon={XCircle} color="text-red-600 bg-red-50" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Historial de Llamadas Outbound ({calls.length})</h3>
        {calls.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Phone size={40} className="mx-auto mb-3 opacity-50" />
            <p>Sin llamadas outbound aun. Usa "Llamar" desde un prospecto o lead.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {calls.map((c, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-900">{c.contact_name || c.to_number}</p>
                  <p className="text-xs text-slate-500">{c.company} | {c.to_number}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'initiated' ? 'bg-green-100 text-green-700' : c.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                    {c.status}
                  </span>
                  <p className="text-xs text-slate-400 mt-1">{new Date(c.created_at).toLocaleString('es')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfigTab({ config, onSave }) {
  const [form, setForm] = useState({
    enabled: config?.enabled ?? true,
    bid_search_enabled: config?.bid_search_enabled ?? true,
    bid_search_frequency: config?.bid_search_frequency ?? 'daily',
    bid_search_keywords: (config?.bid_search_keywords || []).join('\n'),
    prospect_discovery_enabled: config?.prospect_discovery_enabled ?? true,
    prospect_discovery_frequency: config?.prospect_discovery_frequency ?? 'weekly',
    prospect_discovery_sectors: (config?.prospect_discovery_sectors || []).join('\n'),
    auto_followup_enabled: config?.auto_followup_enabled ?? true,
    auto_followup_days: config?.auto_followup_days ?? 7,
    filter_year: config?.filter_year ?? new Date().getFullYear(),
    only_active_processes: config?.only_active_processes ?? true,
    budget_min: config?.budget_min ?? 0,
    budget_max: config?.budget_max ?? 500000,
    min_prospect_score: config?.min_prospect_score ?? 60,
    auto_email_enabled: config?.auto_email_enabled ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        bid_search_keywords: form.bid_search_keywords.split('\n').map(s => s.trim()).filter(Boolean),
        prospect_discovery_sectors: form.prospect_discovery_sectors.split('\n').map(s => s.trim()).filter(Boolean),
      };
      await axios.put(`${API_URL}/api/b2b/auto/config`, payload, { withCredentials: true });
      onSave();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl" data-testid="config-tab">
      <h2 className="text-lg font-semibold text-slate-900 mb-6 flex items-center gap-2"><Gear size={20} /> Configuracion B2B Auto</h2>

      <div className="space-y-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} className="w-5 h-5 rounded" data-testid="enabled-toggle" />
          <div><p className="font-medium text-slate-900">Scheduler Activo</p><p className="text-xs text-slate-500">Habilitar todas las tareas automaticas</p></div>
        </label>

        <hr className="border-slate-200" />

        <div>
          <label className="flex items-center gap-3 mb-3 cursor-pointer">
            <input type="checkbox" checked={form.bid_search_enabled} onChange={e => setForm({ ...form, bid_search_enabled: e.target.checked })} className="w-4 h-4 rounded" />
            <span className="font-medium text-sm">Busqueda de Licitaciones</span>
          </label>
          <div className="ml-7 space-y-3">
            <div>
              <label className="text-xs text-slate-500">Frecuencia</label>
              <select value={form.bid_search_frequency} onChange={e => setForm({ ...form, bid_search_frequency: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm">
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Keywords de busqueda (una por linea)</label>
              <textarea value={form.bid_search_keywords} onChange={e => setForm({ ...form, bid_search_keywords: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm h-32 resize-none font-mono" />
            </div>
          </div>
        </div>

        <hr className="border-slate-200" />

        <div>
          <p className="font-medium text-sm text-slate-900 mb-3">Topes de Presupuesto</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500">Min $</label>
              <input type="number" value={form.budget_min} onChange={e => setForm({ ...form, budget_min: parseInt(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Max $</label>
              <input type="number" value={form.budget_max} onChange={e => setForm({ ...form, budget_max: parseInt(e.target.value) || 500000 })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Score Min</label>
              <input type="number" value={form.min_prospect_score} onChange={e => setForm({ ...form, min_prospect_score: parseInt(e.target.value) || 60 })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" min={0} max={100} />
            </div>
          </div>
          <label className="flex items-center gap-3 mt-3 cursor-pointer">
            <input type="checkbox" checked={form.auto_email_enabled} onChange={e => setForm({ ...form, auto_email_enabled: e.target.checked })} className="w-4 h-4 rounded" />
            <div><p className="text-sm font-medium text-slate-900">Email Automatico a Prospectos</p></div>
          </label>
        </div>

        <hr className="border-slate-200" />

        <div>
          <label className="flex items-center gap-3 mb-3 cursor-pointer">
            <input type="checkbox" checked={form.prospect_discovery_enabled} onChange={e => setForm({ ...form, prospect_discovery_enabled: e.target.checked })} className="w-4 h-4 rounded" />
            <span className="font-medium text-sm">Descubrimiento de Empresas</span>
          </label>
          <div className="ml-7 space-y-3">
            <div>
              <label className="text-xs text-slate-500">Frecuencia</label>
              <select value={form.prospect_discovery_frequency} onChange={e => setForm({ ...form, prospect_discovery_frequency: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm">
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Sectores a buscar (uno por linea)</label>
              <textarea value={form.prospect_discovery_sectors} onChange={e => setForm({ ...form, prospect_discovery_sectors: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm h-32 resize-none font-mono" />
            </div>
          </div>
        </div>

        <hr className="border-slate-200" />

        <div>
          <p className="font-medium text-sm text-slate-900 mb-3">Filtros Generales</p>
          <div className="grid grid-cols-2 gap-4 ml-0">
            <div>
              <label className="text-xs text-slate-500">Filtrar por Ano</label>
              <input type="number" value={form.filter_year} onChange={e => setForm({ ...form, filter_year: parseInt(e.target.value) || new Date().getFullYear() })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" min={2020} max={2030} data-testid="filter-year" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.only_active_processes} onChange={e => setForm({ ...form, only_active_processes: e.target.checked })} className="w-4 h-4 rounded" data-testid="only-active-toggle" />
                <div><p className="text-sm font-medium text-slate-900">Solo Procesos Activos</p><p className="text-xs text-slate-500">Filtrar resultados vencidos</p></div>
              </label>
            </div>
          </div>
        </div>

        <hr className="border-slate-200" />

        <div>
          <label className="flex items-center gap-3 mb-3 cursor-pointer">
            <input type="checkbox" checked={form.auto_followup_enabled} onChange={e => setForm({ ...form, auto_followup_enabled: e.target.checked })} className="w-4 h-4 rounded" />
            <span className="font-medium text-sm">Follow-ups Automaticos</span>
          </label>
          <div className="ml-7">
            <label className="text-xs text-slate-500">Dias sin contacto antes de follow-up</label>
            <input type="number" value={form.auto_followup_days} onChange={e => setForm({ ...form, auto_followup_days: parseInt(e.target.value) || 7 })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" min={1} max={90} />
          </div>
        </div>

        <button onClick={save} disabled={saving} className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-config-btn">
          {saving ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} />} {saving ? 'Guardando...' : 'Guardar Configuracion'}
        </button>
      </div>
    </div>
  );
}
